import React, { useEffect, useRef, useState } from 'react';
import './NewYearEffect.scss';

/**
 * 跨年煙火特效
 * 包含倒數計時和 Canvas 煙火動畫
 */
const NewYearEffect = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [countdown, setCountdown] = useState('');

  // 跨年目標時間
  const getNewYearTarget = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    return new Date(currentYear + 1, 0, 1, 0, 0, 0);
  };

  // 倒數計時
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const target = getNewYearTarget();
      const diff = target - now;

      if (diff <= 0) {
        setCountdown('🎉 Happy New Year! 🎉');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown(`🎆 ${days}天 ${hours}時 ${minutes}分 ${seconds}秒 🎆`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  // Canvas 煙火動畫
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const nextYear = new Date().getFullYear() + 1;
    const str = ['Happy', 'New', 'Year', nextYear.toString(), `Happy New Year ${nextYear}`];
    const normalDuration = 4000;
    const finalDuration = 8000;
    
    const updateSize = () => {
      const parent = canvas.parentElement;
      canvas.width = parent.offsetWidth;
      canvas.height = parent.offsetHeight;
    };
    updateSize();
    
    let w = canvas.width;
    let h = canvas.height;
    
    if (w === 0 || h === 0) return;
    
    let particlesPerChar = 60;
    let chars = [];
    let current = -1;
    const startTime = performance.now();

    const makeChar = (c) => {
      const tmp = document.createElement('canvas');
      const size = tmp.width = tmp.height = 120;
      const tmpCtx = tmp.getContext('2d');
      tmpCtx.font = 'bold 100px Arial';
      tmpCtx.fillStyle = 'white';
      tmpCtx.textBaseline = 'middle';
      tmpCtx.textAlign = 'center';
      tmpCtx.fillText(c, size / 2, size / 2);
      const charData = tmpCtx.getImageData(0, 0, size, size);
      const charParticles = [];
      
      const targetParticles = c === ' ' ? 0 : particlesPerChar;
      
      for (let i = 0; charParticles.length < targetParticles && i < 10000; i++) {
        const x = size * Math.random();
        const y = size * Math.random();
        const offset = parseInt(y) * size * 4 + parseInt(x) * 4;
        if (charData.data[offset])
          charParticles.push([x - size / 2, y - size / 2]);
      }
      return charParticles;
    };

    const makeChars = (t) => {
      const totalDuration = normalDuration * (str.length - 1) + finalDuration;
      const loopTime = t % totalDuration;
      
      let accumulated = 0;
      let actual = 0;
      
      for (let i = 0; i < str.length; i++) {
        const duration = i === str.length - 1 ? finalDuration : normalDuration;
        if (loopTime < accumulated + duration) {
          actual = i;
          break;
        }
        accumulated += duration;
      }
      
      if (current === actual) return;
      current = actual;
      chars = [...str[actual]].map(makeChar);
    };

    const circle = (x, y, r) => {
      ctx.beginPath();
      ctx.ellipse(x, y, r, r, 0, 0, 6.283);
      ctx.fill();
    };

    const rocket = (x, y, id, t) => {
      ctx.fillStyle = 'white';
      const r = 2 - 2 * t + Math.pow(t, 15 * t) * 12;
      y = h - y * t;
      circle(x, y, r);
    };

    const explosion = (pts, x, y, id, t, isFinalText) => {
      const dy = t * t * t * 20;
      let r = Math.sin(id) * 0.5 + 2.5;
      r = t < 0.5 ? (t + 0.5) * t * r : r - (isFinalText ? t * 0.3 : t) * r;
      ctx.fillStyle = `hsl(${id * 55}, 75%, 65%)`;
      pts.forEach((xy, i) => {
        if (i % 8 === 0)
          ctx.fillStyle = `hsl(${id * 55}, 75%, ${65 + t * Math.sin(t * 55 + i) * 30}%)`;
        circle(t * xy[0] + x, h - y + t * xy[1] + dy, r);
      });
    };

    const firework = (t, i, pts) => {
      const isFinalText = current === str.length - 1;
      const stagger = isFinalText ? 0 : i * 300;
      t -= stagger;
      
      const currentDuration = isFinalText ? finalDuration : normalDuration;
      const id = i + chars.length * parseInt(t / currentDuration);
      t = (t % currentDuration) / currentDuration;
      
      let dx, dy;
      if (isFinalText) {
        const charSpacing = Math.min(60, (w * 0.9) / chars.length);
        const totalWidth = (chars.length - 1) * charSpacing;
        dx = (w - totalWidth) / 2 + i * charSpacing;
        dy = h * 0.5;
      } else {
        dx = (i + 1) * w / (1 + chars.length);
        dx += Math.min(0.33, t) * 60 * Math.sin(id);
        dy = h * 0.7;
        dy += Math.sin(id * 4547.411) * h * 0.15;
      }
      
      if (t < 0.33) {
        rocket(dx, dy, id, t * 3);
      } else {
        explosion(pts, dx, dy, id, Math.min(1, Math.max(0, t - 0.33) * 2), isFinalText);
      }
    };

    const render = (timestamp) => {
      const t = timestamp - startTime;
      makeChars(t);
      animationRef.current = requestAnimationFrame(render);
      
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, w, h);
      
      chars.forEach((pts, i) => firework(t, i, pts));
    };

    animationRef.current = requestAnimationFrame(render);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="new-year-effect">
      {countdown && (
        <div className="countdown-display">
          {countdown}
        </div>
      )}
      <canvas ref={canvasRef} className="fireworks-canvas" />
    </div>
  );
};

export default NewYearEffect;

